import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  PaymentsOutlined as MoneyIcon,
  StackedBarChartOutlined as VolumeIcon,
  HowToRegOutlined as HiringIcon,
} from '@mui/icons-material';
import { NavPanel, PanelGroup, PanelLink } from '../../components/layout/NavPanel';
import { useAuthStore } from '../../store/authStore';

// Every report in one section. They used to live inside the section whose
// data they read — payroll's under Payroll, hiring's under Hiring — so the
// numbers someone sits down to study were spread over three menus. Each
// report is still its own screen; this panel is what makes them one place.
//
// What a person sees follows what they may see: payroll and volume are
// the `reports` area; hiring follows the `hiring` area.

export const REPORTS = [
  { to: '/reports/payroll', label: 'Payroll & revenue', icon: <MoneyIcon />, area: 'reports', group: 'Money' },
  { to: '/reports/volume', label: 'Weekly volume', icon: <VolumeIcon />, area: 'reports', group: 'Money' },
  { to: '/reports/hiring', label: 'Hiring', icon: <HiringIcon />, area: 'hiring', group: 'People' },
] as const;

const ReportsPanel: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { canArea } = useAuthStore();
  const allowed = REPORTS.filter((r) => canArea(r.area));
  const groups = Array.from(new Set(allowed.map((r) => r.group)));

  return (
    <NavPanel>
      {groups.map((group) => (
        <PanelGroup key={group} label={group}>
          {allowed.filter((r) => r.group === group).map((r) => (
            <PanelLink key={r.to} to={r.to} icon={r.icon} label={r.label} currentPath={pathname} />
          ))}
        </PanelGroup>
      ))}
      {children}
    </NavPanel>
  );
};

export default ReportsPanel;

/** /reports opens the first report this person may see. */
export const ReportsIndex: React.FC = () => {
  const { canArea } = useAuthStore();
  const first = REPORTS.find((r) => canArea(r.area));
  return <Navigate to={first ? first.to : '/dashboard'} replace />;
};
